import { ValidationService } from './../../../../core/services/validation.service';
import { OrganizationService } from './../../../../core/services/organization.service';
import { RoleService } from './../../../../core/services/role.service';
import { Organization } from './../../../../core/models/organization.model';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModalComponent, UserStore } from 'ccc-commons/core';
import { AccountDTO, OrganizationDTO } from 'src/app/core/dtos/account.dto';
import { GeneralConfigurationService } from '../../../../core/services/general-configuration.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AffiliationField } from '@ccc-core/models/account.model';
import { School } from '@ccc-core/models/school.model';

@Component({
  selector: 'ccc-cca-user-modal',
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.scss']
})
export class UserModalComponent implements OnInit, OnDestroy {
  @ViewChild('content')
  content: NgbModal;
  accountForm: FormGroup;
  affiliations: FormArray;
  modal: any;
  errors = [];

  @Output()
  newAccount = new EventEmitter<AccountDTO>();
  @Output()
  editAccount = new EventEmitter<AccountDTO>();

  @ViewChild('modalSave')
  modalSave: ConfirmModalComponent;

  @ViewChild('externalRefInput')
  externalRefInput!: ElementRef<HTMLInputElement>;

  @Input()
  isEdit = false;

  @Input()
  orgName;

  @Input()
  orgId;


  rolesToShow = [];
  schools = [];
  schoolsToShow = [];
  hasChildren = [];
  titles = [];

  currentOrganizationId = '';
  accountToEdit: AccountDTO;
  index: number;

  private destroySubject: Subject<void>;

  public isLoading: boolean;

  modalOpts: NgbModalOptions = {
    size: 'lg',
    windowClass: 'animated faster zoomIn modal-custom ccc-new-modal',
    backdrop: 'static',
    beforeDismiss: () => {
      if (this.hasUnsavedChanges()) {
        this.modalSave.onOpen();
        return false;
      }
      this.clear();
      return true;
    }
  };

  isSending = false;
  selectedAffiliation = [];
  isAffiliationDeleted = false;
  canAddAnotherAff = false;
  generalErrors = [];
  knownErrorFields = ['email'];
  loadingRoles = [];
  public accountErrors = {
    districtSchoolTitle: []
  };


  constructor(
    private fb: FormBuilder,
    private userStore: UserStore,
    private modalService: NgbModal,
    private roleService: RoleService,
    private organizationService: OrganizationService,
    private configurationService: GeneralConfigurationService
  ) { }

  ngOnInit() {
    this.index = 0;
    this.destroySubject = new Subject<void>();
  }

  open(isEdit = false) {
    this.isEdit = isEdit;
    this.getAccountAffiliations();
    this.createForm();
    this.setAffiliations();

    this.setAffiliationsDeletedState(false);
    this.getTitles();

    this.modal = this.modalService.open(this.content, this.modalOpts);
    this.modal.result.then(
      () => {
        this.accountForm.reset();
        this.selectedAffiliation = [];
      },
      () => {
        this.accountForm.reset();
        this.accountForm.removeControl('affiliations');
        this.selectedAffiliation = [];
      }
    );
    setTimeout(() => {
      this.externalRefInput.nativeElement.focus();
    });
  }

  openToEdit(accountToEdit) {
    this.accountToEdit = accountToEdit;
    this.open(true);
  }

  /**
   * setup the form and the data when a user is going to be edited
   * @param accountToEdit the account to be updated
   */
  private setModalToEdit(accountToEdit): void {
    this.accountForm.controls.externalRef.setValue(accountToEdit.externalRef);
    this.accountForm.controls.firstName.setValue(accountToEdit.firstName);
    this.accountForm.controls.lastName.setValue(accountToEdit.lastName);
    this.accountForm.controls.email.setValue(accountToEdit.email);
    this.accountForm.controls.title.setValue(accountToEdit.title);
    this.accountForm.controls.leadershipFlag.setValue(accountToEdit.leadershipFlag);
    this.accountForm.controls.districtSchoolTitle.setValue(accountToEdit.districtSchoolTitle);

    // disable for edit
    this.accountForm.controls.email.disable();

    this.affiliations = this.accountForm.get('affiliations') as FormArray;
    this.affiliations.removeAt(0);
    this.isLoading = false;

    let index = 0;

    const withoutChildren: Organization[] = [];
    for (const org of accountToEdit.organizations) {
      if (org.name === 'Root' && org.role === null) {
        continue;
      }
      let isChildren = false;

      for (const notChildOrg of withoutChildren) {
        if (notChildOrg.includeChilds &&
          org.path.includes(notChildOrg.path) &&
          org.level > notChildOrg.level

        ) {
          isChildren = true;
          break;

        }
      }
      if (!isChildren) {
        withoutChildren.push(org);
        this.affiliations.push(this.createAffiliationFromOrganization(org));
        this.loadSchools();
        this.onAffiliationSelected(index);
      }
      index++;
    }
  }

  hasUnsavedChanges() {
    if (this.isAffiliationDeleted || this.accountForm.dirty) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * createForm
   * creates the form object with the fields required
   */
  private createForm(): void {
    this.accountForm = this.fb.group({
      externalRef: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, ValidationService.emailValidator]],
      title: [null, Validators.required],
      leadershipFlag: [''],
      districtSchoolTitle: [''],
      affiliations: this.fb.array([this.createAffiliation()]),
    });
  }

  createAffiliation() {
    return this.fb.group({
      role: ['', Validators.required],
      affiliation: ['', Validators.required],
      includeChildren: [false]
    });
  }

  createAffiliationFromOrganization(org: Organization) {
    return this.fb.group({
      role: [((org.role ? org.role.id : '')), Validators.required],
      affiliation: [org.id, Validators.required],
      includeChildren: [org.includeChilds]

    });
  }

  addAnotherAffiliation() {
    this.setAffiliations();
    this.affiliations.push(this.createAffiliation());
    this.schoolsToShow[this.affiliations.controls.length - 1] = this.schools;
    this.updateSelectableAffiliation();
  }

  removeAffiliation(index) {

    const selectedAff = this.affiliations.controls[index]['controls'].affiliation
      .value;
    this.selectedAffiliation[index] = selectedAff;


    this.schools = this.schools.map(school => {
      if (school.id === selectedAff) {
        school.selected = false;
      }
      return school;
    });

    this.setAffiliations();
    this.affiliations.removeAt(index);
    this.updateSelectableAffiliation();
    this.setAffiliationsDeletedState(true);
    this.rolesToShow.splice(index, 1);
  }


  onAffiliationSelected(index: number) {
    this.setAffiliations();
    this.loadingRoles[index] = true;

    const selectedAff = this.affiliations.controls[index]['controls'].affiliation
      .value;
    this.selectedAffiliation[index] = selectedAff;

    this.roleService.getRoles(this.selectedAffiliation[index]).subscribe(response => {
      this.rolesToShow[index] = response.body;
      this.loadingRoles[index] = false;
    });

    this.updateSelectableAffiliation();
  }

  /**
   * getAllDescendants
   * get all the descendants of a school
   * @param school the school to get the descendants from
   * @param allSchools all the schools to search in
   * @returns an array with all the descendants
   */
  private getAllDescendants(school: AffiliationField, allSchools: School[]): School[] {
    let descendants: School[] = [];

    function findDescendants(parent: any) {
      let children = allSchools.filter(s => s.path.includes(parent.path) && s.level > parent.level);

      for (let child of children) {
        if (!descendants.includes(child)) {
          descendants.push(child);
          findDescendants(child);
        }
      }
    }

    findDescendants(school);
    return descendants;
  }

  /**
   * updateSelectableAffiliation
   * checks which organizations are selected, checks if they have 'includeChild'
   * and removes value for child selected
   */
  private updateSelectableAffiliation(): void {
    this.canAddAnotherAff = true;

    this.setAffiliations();
    this.loadSchools();

    for (let i = 0; i < this.affiliations.controls.length; i++) {
      const selectedAff = this.affiliations.controls[i]['controls'].affiliation.value;
      const selectedOrg = this.schools.find(school => school.id === selectedAff);
      let allDescendants: School[] = [];

      if (selectedOrg) {
        allDescendants = this.getAllDescendants(selectedOrg, this.schools);
      }

      this.hasChildren[i] = allDescendants.length > 0;

      for (let j = this.affiliations.controls.length - 1; j >= 0; j--) {
        if (i === j) continue;

        let wasDescendant = false;

        if (this.affiliations.controls[i]['controls'].includeChildren.value === true) {
          this.schoolsToShow[j] = this.schoolsToShow[j].filter(
            school => !allDescendants.some(descendant => school.id === descendant.id)
          );

          if (allDescendants.some(descendant => descendant.id === this.affiliations.controls[j]['controls'].affiliation.value)) {
            wasDescendant = true;
          }
        }

        if (this.schoolsToShow[j].length === 0 || wasDescendant) {
          this.affiliations.removeAt(j);
          this.hasChildren.splice(j, 1);
          this.selectedAffiliation.splice(j, 1);
          this.rolesToShow.splice(j, 1);
          this.canAddAnotherAff = false;
          this.schoolsToShow.splice(j, 1);
        }
      }
    }
  }

  private setAffiliations() {
    this.affiliations = this.accountForm.get('affiliations') as FormArray;
  }

  private setAffiliationsDeletedState(wasDeleted: boolean) {
    this.isAffiliationDeleted = wasDeleted;
  }

  /**
   * onSubmit
   * when the user confirms to save the changes or the new account
   */
  public onSubmit(): void {
    this.isSending = true;
    this.validateForm();
    if (this.accountForm.valid) {
      const controls = this.accountForm.controls;
      const rawLeadershipFlag = controls.leadershipFlag.value;
      const leadershipFlag = !!rawLeadershipFlag;
      if (this.isEdit) {
        const editAccount: AccountDTO = new AccountDTO(
          controls.firstName.value,
          controls.lastName.value,
          this.accountToEdit.status,
          [],
          this.accountToEdit.id,
          controls.externalRef.value,
          this.accountToEdit.email,
          controls.title.value,
          '',
          leadershipFlag,
          controls.districtSchoolTitle.value || '',
        );

        this.loadOrganizations(editAccount);
        this.editAccount.emit(editAccount);
      } else {
        const newAccount = {
          externalRef: controls.externalRef.value,
          firstName: controls.firstName.value,
          lastName: controls.lastName.value,
          status: 'Active',
          email: controls.email.value,
          title: controls.title.value,
          organizationId: this.currentOrganizationId,
          organizations: [],
          leadershipFlag: leadershipFlag,
          districtSchoolTitle: controls.districtSchoolTitle.value || '',
        };
        this.loadOrganizations(newAccount);

        this.newAccount.emit(newAccount);
      }
    }
  }

  loadOrganizations(account) {
    this.setAffiliations();

    for (let i = 0; i < this.affiliations.controls.length; i++) {
      const selectedAff = this.affiliations.controls[i]['controls']
        .affiliation.value;
      const selectedRole = this.affiliations.controls[i]['controls'].role
        .value;
      const includeChildren = this.affiliations.controls[i]['controls'].includeChildren
        .value;

      account.organizations.push(
        new OrganizationDTO(selectedAff, selectedRole, includeChildren)
      );
    }

  }

  onClose() {
    this.isSending = false;
    this.isEdit = false;
    this.setAffiliationsDeletedState(false);
    this.clear();
    this.modal.close();
  }

  validateForm() {
    Object.keys(this.accountForm.controls).forEach(key => {
      if (this.accountForm.get(key).hasError('required')) {
        this.accountForm.get(key).markAsTouched();
      }
    });
  }

  loadSchools() {
    this.schools.map(school => { school.selected = false; return school; });
    for (let i = 0; i < this.affiliations.controls.length; i++) {
      this.schoolsToShow[i] = this.schools;
    }
  }

  ngOnDestroy() {
    if (this.modal) {
      this.modal.close();
    }
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  onCloseWithoutSaving(close) {
    if (close) {
      this.clear();
      this.modal.close();
    }
  }

  handleErrors(error) {

    this.errors = [];
    this.isSending = false;
    const errorObj = error.error;

    if (Array.isArray(errorObj.message)) {
      for (const msg of errorObj.message) {

        this.errors[msg.field] = [];
        for (const err of msg.errors) {
          if (this.knownErrorFields.includes(msg.field)) {
            this.errors[msg.field].push(err);
          } else {
            this.generalErrors.push(err.charAt(0).toUpperCase() + err.slice(1));

          }
        }
      }
    } else {
      this.errors[errorObj.field] = [];
      this.errors[errorObj.field].push(errorObj.message);
    }

  }

  onDismiss() {
    this.modal.dismiss();
  }
  clear() {
    this.errors = [];
    this.rolesToShow = [];
    this.selectedAffiliation = [];
    this.generalErrors = [];
    this.isEdit = false;
  }

  /**
   * onIncludeChildren
   * updated the list of organizations that can be selected
   * delete the children organizations if they are below
   */
  public onIncludeChildren(): void {
    this.updateSelectableAffiliation();
    for (let i = this.affiliations.controls.length - 1; i >= 0; i--) {
      if (this.affiliations.controls[i]['controls'].affiliation.value === '' &&
        this.affiliations.controls[i]['controls'].role.value === '' &&
      this.selectedAffiliation[i] === '') {
        this.affiliations.removeAt(i);
      }
    }
  }

  getAccountAffiliations() {
    let id = '';
    if (this.isEdit) {
      id = this.accountToEdit.organizationId;
      this.isLoading = true;
    } else {
      this.currentOrganizationId = this.orgId;
      id = this.orgId;
    }

    this.organizationService.getOrganizationById(id)
      .pipe(
        takeUntil(this.destroySubject),
      )
      .subscribe(response => {
        this.schools = [];
        this.schools.push(response.body);
        this.processAffiliationResponse(this.schools);
        this.loadSchools();

        if (this.isEdit) {
          this.setModalToEdit(this.accountToEdit);
        }
      });

  }

  private processAffiliationResponse(response) {
    this.schools = this.organizationService.getAffiliationsSorted(response);
  }

  getTitles() {
    this.configurationService.getAllConfig()
      .subscribe(response => {
        for (const config of response['body']) {
          if (config.type === 'title') {
            this.titles = config.values;
            break;
          }
        }
      });
  }

  /**
   * It verifies if an affiliation is already selected
   * in order to show it disabled
   * @param aff an affiliation
   */
  public isAffiliationSelected(aff): boolean {
    return !!this.affiliations.controls.
    find(control => control.value.affiliation && control.value.affiliation === aff.id);
  }

  /**
   * checkDistrictSchoolTitle
   * check the length of the districtSchoolTitle and show an error if
   * it is necessary
   */
  public checkDistrictSchoolTitle(): void {
    this.accountErrors.districtSchoolTitle = [];
    if ((this.accountForm.controls.districtSchoolTitle.value.length < 3 &&
        this.accountForm.controls.districtSchoolTitle.value.length > 0) ||
      this.accountForm.controls.districtSchoolTitle.value.length > 50) {
      this.accountErrors.districtSchoolTitle.push('The length must be between 3 and 50');
    }
  }

  /**
   * disabledButton
   * returns a boolean based on what conditions are true to enable or disable
   * the save button
   * @returns `boolean` true if it is disabled
   */
  public disabledButton(): boolean {
    return !this.accountForm.valid ||
      this.isSending ||
      !this.hasUnsavedChanges();
  }

  /**
   * isIncludingAllLocations
   * checks if the user is including all the locations in the affiliations
   * @returns `boolean` true if the user is including all the locations
   */
  public isIncludingAllLocations(): boolean {
    return this.affiliations.controls.some(control => control.value.includeChildren);
  }

  /**
   * doesNotHaveAffiliation
   * returns `true` if the account does not have any affiliation selected
   */
  private doesNotHaveAffiliation(): boolean {
    return this.selectedAffiliation.length === 0 ||
      this.selectedAffiliation.length === 1 && this.selectedAffiliation[0] === '';
  }

  /**
   * showAddAnotherAffiliation
   * checks if the user can add another affiliation
   * @returns `boolean` true if the user can add another affiliation
   */
  public showAddAnotherAffiliation(): boolean {
    return this.canAddAnotherAff && !this.isIncludingAllLocations() && !this.doesNotHaveAffiliation();
  }
}
